import React from 'react'
import { useTheme, lighten } from '@mui/material'

import { Line } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js'
import { constants } from '../../config'

const { catNames } = constants

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend)

const ClientDataChart = ({ props: viewProps = {} }) => {
  const {
    clientData = { datasets: [], labels: [] },
    graphOptions = { graphTitle: '', graphTitleX: '', graphTitleY: '', tooltipOptions: null },
    activeTab = ''
  } = viewProps
  const theme = useTheme()

  const defaultTooltipPlugin = { mode: 'index', intersect: false }
  const tooltipPlugin = activeTab === catNames.AI_SP_MONTHLY || activeTab === catNames.SP_MONTHLY ? graphOptions.tooltipOptions || defaultTooltipPlugin : defaultTooltipPlugin

  const calculateStats = React.useMemo(() => {
    const { datasets = [], labels = [] } = clientData
    if (!datasets.length || !labels.length) return { average: 0, max: 0 }

    const allValues = []
    datasets.forEach((dataset) => {
      if (dataset?.label === 'Average' || dataset?.label === 'Max') return
      if (Array.isArray(dataset.data)) {
        dataset.data.forEach((value) => {
          const num = Number(value)
          if (!isNaN(num) && num > 0) allValues.push(num)
        })
      }
    })

    if (allValues.length === 0) return { average: 0, max: 0 }
    const sum = allValues.reduce((acc, val) => acc + val, 0)
    return { average: sum / allValues.length, max: Math.max(...allValues) }
  }, [clientData])

  const enhancedData = React.useMemo(() => {
    const { datasets = [], labels = [] } = clientData
    const { average, max } = calculateStats

    const statDatasets = []

    if (average > 0 && labels.length > 0) {
      statDatasets.push({
        label: 'Average',
        data: labels.map(() => average),
        borderColor: theme.palette.info.main,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0,
        hidden: false
      })
    }

    if (max > 0 && labels.length > 0) {
      statDatasets.push({
        label: 'Max',
        data: labels.map(() => max),
        borderColor: theme.palette.warning.main,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [8, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0,
        hidden: false
      })
    }

    return {
      labels,
      datasets: [...datasets, ...statDatasets]
    }
  }, [clientData, calculateStats, theme])

  const statLabelsPlugin = React.useMemo(() => ({
    id: 'statLabels',
    afterDraw: (chart) => {
      const { average, max } = calculateStats
      const ctx = chart.ctx
      const chartArea = chart.chartArea

      if (!chartArea || (!average && !max)) return

      ctx.save()
      ctx.font = 'bold 12px sans-serif'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'middle'

      const datasets = chart.data.datasets || []
      let averageIndex = -1
      let maxIndex = -1

      datasets.forEach((dataset, index) => {
        if (dataset.label === 'Average') averageIndex = index
        if (dataset.label === 'Max') maxIndex = index
      })

      if (averageIndex >= 0 && average > 0) {
        const meta = chart.getDatasetMeta(averageIndex)
        if (meta && meta.data && meta.data.length > 0) {
          const lastPoint = meta.data[meta.data.length - 1]
          const x = chartArea.right - 5
          const y = lastPoint.y

          const text = `Avg: ${average.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
          const textMetrics = ctx.measureText(text)
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
          ctx.fillRect(x - textMetrics.width - 8, y - 10, textMetrics.width + 8, 20)

          ctx.fillStyle = theme.palette.info.main
          ctx.fillText(text, x, y)
        }
      }

      if (maxIndex >= 0 && max > 0) {
        const meta = chart.getDatasetMeta(maxIndex)
        if (meta && meta.data && meta.data.length > 0) {
          const lastPoint = meta.data[meta.data.length - 1]
          const x = chartArea.right - 5
          const y = lastPoint.y

          const text = `Max: ${max.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
          const textMetrics = ctx.measureText(text)
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
          ctx.fillRect(x - textMetrics.width - 8, y - 10, textMetrics.width + 8, 20)

          ctx.fillStyle = theme.palette.warning.main
          ctx.fillText(text, x, y)
        }
      }

      ctx.restore()
    }
  }), [calculateStats, theme])

  const options = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: graphOptions.graphTitle,
        color: '#FFF',
        font: { size: 22, weight: 'bolder' },
        padding: { top: 10 }
      },
      tooltip: { ...tooltipPlugin },
      legend: {
        labels: {
          color: '#FFF',
          font: { weight: 'bold' },
          filter: (legendItem) => legendItem.text !== 'Average' && legendItem.text !== 'Max'
        }
      }
    },
    scales: {
      x: {
        type: 'category',
        title: { display: true, text: graphOptions.graphTitleX, color: '#FFF', font: { size: 18, weight: 'bold' }, padding: { top: 10, bottom: 20 } },
        ticks: { color: '#FFF', font: { size: 12 } },
        grid: { color: lighten(theme.custom.keysys.dark, 0.2), borderColor: 'black', borderWidth: 1000, lineWidth: 1 }
      },
      y: {
        title: { display: true, text: graphOptions.graphTitleY, color: '#FFF', font: { size: 18, weight: 'bold' }, padding: { top: 20, bottom: 10 } },
        ticks: { color: '#FFF', font: { size: 12 } },
        grid: { color: lighten(theme.custom.keysys.dark, 0.2), borderColor: 'black', borderWidth: 1, lineWidth: 1 },
        min: 0
      }
    }
  }), [clientData, graphOptions.graphTitle, graphOptions.graphTitleX, graphOptions.graphTitleY, tooltipPlugin, theme])

  return (
    <Line data={enhancedData} options={options} plugins={[statLabelsPlugin]} redraw />
  )
}

export { ClientDataChart }
